import { useCallback, useEffect, useRef, useState } from 'react';

import { isAxiosError } from 'axios';

import { AttachmentRef } from '@/features/Feedback/types';

import { useCopyRepositoryDocument } from './api';
import { RelatedDocument, RepositoryDocument } from './types';

export const MAX_RELATED_DOCUMENTS = 10;
const MAX_PARALLEL_COPIES = 2;
const TIMEOUT_MESSAGE = 'This file took too long to copy. Try a smaller file.';
const GENERIC_MESSAGE = 'Could not attach this file.';

const copyErrorMessage = (error: unknown): string =>
    isAxiosError(error) && error.response?.status === 504
        ? TIMEOUT_MESSAGE
        : GENERIC_MESSAGE;

const sameFile = (item: RelatedDocument, fileId: number): boolean =>
    item.document.fileId === fileId;

const isCopying = (item: RelatedDocument, fileId: number): boolean =>
    sameFile(item, fileId) && item.status === 'copying';

/** The state a newly added document starts in, reusing a sibling's finished copy. */
function initialItem(
    document: RepositoryDocument,
    items: RelatedDocument[],
): RelatedDocument {
    const copied = items.find(
        (item) => sameFile(item, document.fileId) && item.status === 'ready',
    );
    return copied
        ? { document, status: 'ready', ref: copied.ref }
        : { document, status: 'copying' };
}

/** Settle every document of a file that is still waiting on its copy. */
function settleFile(
    items: RelatedDocument[],
    fileId: number,
    outcome: Pick<RelatedDocument, 'status' | 'ref' | 'errorMessage'>,
): RelatedDocument[] {
    return items.map((item) =>
        isCopying(item, fileId) ? { ...item, ...outcome } : item,
    );
}

const uniqueRefs = (items: RelatedDocument[]): AttachmentRef[] => {
    const byKey = new Map<string, AttachmentRef>();
    items.forEach((item) => {
        if (item.status === 'ready' && item.ref) byKey.set(item.ref.key, item.ref);
    });
    return [...byKey.values()];
};

/**
 * Tracks the documents picked as related documents and copies each distinct
 * file to the license server, at most two at a time. A file shared by several
 * documents is copied once and counts as added for all of them.
 */
export function useRelatedDocuments(limit = MAX_RELATED_DOCUMENTS) {
    const copy = useCopyRepositoryDocument();
    const copyRef = useRef(copy.mutateAsync);
    copyRef.current = copy.mutateAsync;

    const [items, setItems] = useState<RelatedDocument[]>([]);
    const itemsRef = useRef<RelatedDocument[]>([]);
    const queueRef = useRef<number[]>([]);
    const activeRef = useRef(new Set<number>());
    const disposedRef = useRef(false);

    // A closed form stops starting queued copies; one already sent still finishes.
    useEffect(() => {
        disposedRef.current = false;
        const queue = queueRef.current;
        return () => {
            disposedRef.current = true;
            queue.length = 0;
        };
    }, []);

    const commit = useCallback((next: RelatedDocument[]) => {
        itemsRef.current = next;
        setItems(next);
    }, []);

    const pump = useCallback(() => {
        const startNext = (): void => {
            if (disposedRef.current || activeRef.current.size >= MAX_PARALLEL_COPIES) return;
            const fileId = queueRef.current.shift();
            if (fileId === undefined) return;
            const waiting = itemsRef.current.find((i) => isCopying(i, fileId));
            if (waiting) runCopy(fileId, waiting.document.id);
            startNext();
        };

        const finish = (fileId: number, outcome: Parameters<typeof settleFile>[2]) => {
            activeRef.current.delete(fileId);
            commit(settleFile(itemsRef.current, fileId, outcome));
            startNext();
        };

        const runCopy = (fileId: number, docId: number): void => {
            activeRef.current.add(fileId);
            copyRef
                .current(docId)
                .then((ref) => finish(fileId, { status: 'ready', ref }))
                .catch((error: unknown) =>
                    finish(fileId, {
                        status: 'error',
                        errorMessage: copyErrorMessage(error),
                    }),
                );
        };

        startNext();
    }, [commit]);

    const isQueuedOrActive = (fileId: number): boolean =>
        activeRef.current.has(fileId) || queueRef.current.includes(fileId);

    const add = useCallback(
        (document: RepositoryDocument) => {
            const current = itemsRef.current.filter(
                (i) => !(i.document.id === document.id && i.status === 'error'),
            );
            const alreadyAdded = current.some((i) => i.document.id === document.id);
            if (alreadyAdded || current.length >= limit) return;

            const item = initialItem(document, current);
            commit([...current, item]);
            if (item.status === 'copying' && !isQueuedOrActive(document.fileId)) {
                queueRef.current.push(document.fileId);
                pump();
            }
        },
        [commit, limit, pump],
    );

    const remove = useCallback(
        (docId: number) => {
            commit(itemsRef.current.filter((i) => i.document.id !== docId));
        },
        [commit],
    );

    const isAdded = useCallback(
        (document: RepositoryDocument): boolean =>
            items.some(
                (i) => sameFile(i, document.fileId) && i.status !== 'error',
            ),
        [items],
    );

    return {
        items,
        add,
        remove,
        isCopying: items.some((i) => i.status === 'copying'),
        readyRefs: uniqueRefs(items),
        isFull: items.length >= limit,
        isAdded,
    };
}
